import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Mail, Shield, UserCog, Trash2, Edit2, 
  Loader2, CheckCircle, XCircle, ArrowLeft,
  Key, UserPlus, Eye
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminTeam() {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [creating, setCreating] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "product_manager"
  });
  const [newPassword, setNewPassword] = useState("");

  // Filter team members by role
  const filteredMembers = roleFilter === "all" 
    ? teamMembers 
    : teamMembers.filter(m => m.role === roleFilter);

  const fetchTeamMembers = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.team_members || []);
      } else {
        toast.error("Failed to fetch team members");
      }
    } catch (error) {
      toast.error("Error loading team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const handleCreateMember = async () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error("Please fill all required fields");
      return;
    }

    setCreating(true);
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/team-members`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Team member created successfully");
        setShowCreateModal(false);
        setFormData({ email: "", password: "", name: "", role: "product_manager" });
        fetchTeamMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create team member");
      }
    } catch (error) {
      toast.error("Error creating team member");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/team-members/${selectedMember.team_member_id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          role: formData.role,
          is_active: selectedMember.is_active
        })
      });

      if (response.ok) {
        toast.success("Team member updated");
        setShowEditModal(false);
        fetchTeamMembers();
      } else {
        toast.error("Failed to update team member");
      }
    } catch (error) {
      toast.error("Error updating team member");
    }
  };

  const handleDeleteMember = async (member) => {
    if (!confirm(`Are you sure you want to delete ${member.name}? All their assigned leads will be unassigned.`)) {
      return;
    }

    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/team-members/${member.team_member_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Team member deleted");
        fetchTeamMembers();
      } else {
        toast.error("Failed to delete team member");
      }
    } catch (error) {
      toast.error("Error deleting team member");
    }
  };

  const handleResetPassword = async () => {
    if (!selectedMember || !newPassword) return;

    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(
        `${API}/api/admin/team-members/${selectedMember.team_member_id}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success("Password reset successfully");
        setShowResetPasswordModal(false);
        setNewPassword("");
      } else {
        toast.error("Failed to reset password");
      }
    } catch (error) {
      toast.error("Error resetting password");
    }
  };

  const handleToggleActive = async (member) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/team-members/${member.team_member_id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ is_active: !member.is_active })
      });

      if (response.ok) {
        toast.success(member.is_active ? "Member deactivated" : "Member activated");
        fetchTeamMembers();
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  // Emulate team member - login as them
  const handleEmulateMember = async (member) => {
    const token = localStorage.getItem("admin_token");
    const adminEmail = localStorage.getItem("admin_email");
    
    try {
      const response = await fetch(`${API}/api/admin/team-members/${member.team_member_id}/emulate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || "Failed to emulate team member");
        return;
      }
      
      const data = await response.json();
      
      // Store original admin token for returning later
      localStorage.setItem("admin_original_token", token);
      localStorage.setItem("admin_original_email", adminEmail);
      
      // Set the emulation token as the admin token
      localStorage.setItem("admin_token", data.emulation_token);
      localStorage.setItem("admin_email", data.team_member.email);
      localStorage.setItem("admin_role", data.team_member.role);
      localStorage.setItem("admin_name", data.team_member.name);
      localStorage.setItem("admin_team_member_id", data.team_member.team_member_id);
      
      // Set emulation mode flag
      localStorage.setItem("emulation_mode", "true");
      localStorage.setItem("emulated_user_email", data.team_member.email);
      localStorage.setItem("emulated_user_type", "team_member");
      
      toast.success(data.message);
      
      // Navigate to the appropriate page based on role
      // CRM-only roles (product_manager, sales, marketing) go to CRM
      // Admin role gets full admin access
      const crmOnlyRoles = ["product_manager", "sales", "marketing"];
      if (crmOnlyRoles.includes(data.team_member.role)) {
        navigate("/admin/crm");
      } else {
        navigate("/admin");
      }
    } catch (error) {
      console.error("Error emulating team member:", error);
      toast.error("Failed to start emulation");
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      email: member.email,
      password: "",
      name: member.name,
      role: member.role
    });
    setShowEditModal(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "product_manager": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "sales": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "marketing": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default: return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin": return "Admin";
      case "product_manager": return "Product Manager";
      case "sales": return "Sales";
      case "marketing": return "Marketing";
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-400" />
                Team Management
              </h1>
              <p className="text-zinc-500 mt-1">Manage admin and product manager accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700" data-testid="role-filter">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="product_manager">Product Manager</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => {
                setFormData({ email: "", password: "", name: "", role: "product_manager" });
                setShowCreateModal(true);
              }}
              className="bg-[#14A800] hover:bg-[#128A00]"
              data-testid="create-team-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>

        {/* Team Members Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
            <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-300 mb-2">
              {roleFilter === "all" ? "No Team Members Yet" : `No ${getRoleLabel(roleFilter)} Team Members`}
            </h3>
            <p className="text-zinc-500 mb-6">
              {roleFilter === "all" ? "Add product managers to help manage leads" : "No team members match this filter"}
            </p>
            {roleFilter === "all" && (
              <Button onClick={() => setShowCreateModal(true)} className="bg-[#14A800] hover:bg-[#128A00]">
                <UserPlus className="w-4 h-4 mr-2" />
                Add First Team Member
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredMembers.map((member) => (
              <div
                key={member.team_member_id}
                className={`bg-white/5 border rounded-xl p-5 hover:bg-white/10 transition-colors ${
                  member.is_active ? "border-white/10" : "border-red-500/30 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      member.role === "admin" ? "bg-red-500/20" : "bg-blue-500/20"
                    }`}>
                      {member.role === "admin" ? (
                        <Shield className="w-6 h-6 text-red-400" />
                      ) : (
                        <UserCog className="w-6 h-6 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{member.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getRoleColor(member.role)}`}>
                          {getRoleLabel(member.role)}
                        </span>
                        {!member.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {member.email}
                        </span>
                        {member.last_login && (
                          <span>Last login: {new Date(member.last_login).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Direct Action Buttons with Tooltips */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEmulateMember(member)}
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      data-testid={`emulate-member-${member.team_member_id}`}
                      title="Emulate"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(member)}
                      className="text-zinc-400 hover:text-white hover:bg-white/10"
                      data-testid={`edit-member-${member.team_member_id}`}
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedMember(member);
                        setShowResetPasswordModal(true);
                      }}
                      className="text-zinc-400 hover:text-white hover:bg-white/10"
                      data-testid={`reset-password-${member.team_member_id}`}
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(member)}
                      className={member.is_active ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-green-400 hover:text-green-300 hover:bg-green-500/10"}
                      data-testid={`toggle-active-${member.team_member_id}`}
                      title={member.is_active ? "Deactivate" : "Activate"}
                    >
                      {member.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteMember(member)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`delete-member-${member.team_member_id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a new admin or product manager account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="product_manager">Product Manager (CRM Only, No AI Call)</SelectItem>
                    <SelectItem value="sales">Sales (CRM + AI Call)</SelectItem>
                    <SelectItem value="marketing">Marketing (CRM + AI Call + Campaigns)</SelectItem>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">
                  Product Manager: CRM only • Sales: CRM + AI Call • Marketing: CRM + AI Call + Campaigns
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleCreateMember} disabled={creating} className="bg-[#14A800] hover:bg-[#128A00]">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create Member
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={formData.email} disabled className="bg-zinc-800 border-zinc-700 opacity-50" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="product_manager">Product Manager</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleUpdateMember} className="bg-blue-600 hover:bg-blue-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Set a new password for {selectedMember?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetPasswordModal(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
